import { useTranslation } from 'react-i18next';

type PaginationHeaderProps = {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
};

export const PaginationHeader: React.FC<PaginationHeaderProps> = ({
  page,
  perPage,
  total,
  onPageChange,
}) => {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const { t } = useTranslation();

  const getPages = () => {
    const pageNumberToShow = 5;
    const edgePageCount = 2;

    if (lastPage <= pageNumberToShow + edgePageCount) {
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const startPages = [1, 2];
    const endPages = [lastPage - 1, lastPage];

    let middleStart = Math.max(page - 1, 3);
    let middleEnd = Math.min(page + 1, lastPage - 2);

    if (page <= 4) {
      middleStart = 3;
      middleEnd = 5;
    } else if (page >= lastPage - 3) {
      middleStart = lastPage - 4;
      middleEnd = lastPage - 2;
    }

    pages.push(...startPages);

    if (middleStart > 3) {
      pages.push('...');
    }

    for (let i = middleStart; i <= middleEnd; i++) {
      pages.push(i);
    }

    if (middleEnd < lastPage - 2) {
      pages.push('...');
    }

    pages.push(...endPages);

    return [...new Set(pages)];
  };

  const pages = getPages();

  return (
    <div className="mt-4 flex flex-1 flex-row justify-between">
      <div>
        <span className="pl-1">
          {`${t('wf.common.all')} ${total} ${total === 1 ? t('wf.common.item') : t('wf.common.items')} ＜${page}/${lastPage}＞`}
        </span>
      </div>
      <div className="flex flex-row items-center space-x-4">
        <button
          className="p-1 text-sm font-semibold text-blue-600 visited:bg-purple-600 visited:text-purple-600 hover:enabled:underline disabled:text-gray-400"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
        >
          {t('wf.common.previous')}
        </button>
        <div className="flex space-x-1">
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={`${p}-${i}`} className="px-1 text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={p}
                className={`rounded px-2 py-1 text-sm font-semibold ${
                  p === page ? 'bg-gray-300 text-gray-700' : 'text-gray-600 hover:underline'
                }`}
                onClick={() => onPageChange(p as number)}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          className="p-1 text-sm font-semibold text-blue-600 visited:bg-purple-600 visited:text-purple-600 hover:enabled:underline disabled:text-gray-400"
          onClick={() => onPageChange(Math.min(lastPage, page + 1))}
          disabled={page === lastPage}
        >
          {t('wf.common.next')}
        </button>
      </div>
    </div>
  );
};
